/**
 * ServiceEEATProof — Visible E-E-A-T proof section. No hidden content.
 */
import { Shield, Award, Users, Globe, Clock, CheckCircle2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContextNew';
import { SITE_URL } from '@/lib/seo';
import { motion } from 'framer-motion';
import { useSchemaDOM } from '@/hooks/useSchemaDOM';
import { DeferredSchemas } from './DeferredSchemas';
import { BreadcrumbAutoGenerator } from './BreadcrumbAutoGenerator';

import { ServiceVideoSchema } from './ServiceVideoSchema';
import { ServiceCompetitorSchema } from './ServiceCompetitorSchema';
import { ServiceEnrichedSchemas } from './ServiceEnrichedSchemas';
import { ServiceAuthoritySchemas } from './ServiceAuthoritySchemas';

const LABELS: Record<string, {
  title: string; sub: string;
  clients: string; agents: string; languages: string; csat: string; sla: string; deploy: string;
  iso: string; gdpr: string; pci: string;
}> = {
  en: { title: 'Why Companies Trust Us', sub: 'Verified metrics, globally certified, enterprise-ready.', clients: 'Clients Worldwide', agents: 'Trained Agents', languages: 'Languages', csat: 'CSAT Score', sla: 'Uptime SLA', deploy: 'Day Deployment', iso: 'ISO 27001 Certified', gdpr: 'GDPR Compliant', pci: 'PCI-DSS Level 1' },
  fr: { title: 'Pourquoi Nous Faire Confiance', sub: 'Métriques vérifiées, certifications mondiales, prêt pour l\'entreprise.', clients: 'Clients Mondiaux', agents: 'Agents Formés', languages: 'Langues', csat: 'Score CSAT', sla: 'SLA Disponibilité', deploy: 'Jours Déploiement', iso: 'Certifié ISO 27001', gdpr: 'Conforme RGPD', pci: 'PCI-DSS Niveau 1' },
  es: { title: 'Por Qué Confiar en Nosotros', sub: 'Métricas verificadas, certificaciones globales.', clients: 'Clientes Globales', agents: 'Agentes Capacitados', languages: 'Idiomas', csat: 'Puntuación CSAT', sla: 'SLA Disponibilidad', deploy: 'Días de Despliegue', iso: 'Certificación ISO 27001', gdpr: 'Cumple RGPD', pci: 'PCI-DSS Nivel 1' },
  ar: { title: 'لماذا تثق بنا الشركات', sub: 'مؤشرات موثقة وشهادات عالمية وجاهزية للمؤسسات.', clients: 'عملاء حول العالم', agents: 'وكلاء مدربون', languages: 'اللغات', csat: 'نتيجة CSAT', sla: 'اتفاقية التوفر', deploy: 'أيام الإطلاق', iso: 'شهادة ISO 27001', gdpr: 'متوافق مع GDPR', pci: 'PCI-DSS المستوى 1' },
  de: { title: 'Warum Unternehmen Uns Vertrauen', sub: 'Verifizierte Kennzahlen, global zertifiziert, enterprise-ready.', clients: 'Kunden Weltweit', agents: 'Geschulte Agenten', languages: 'Sprachen', csat: 'CSAT-Wert', sla: 'Uptime-SLA', deploy: 'Tage bis Go-Live', iso: 'ISO 27001 Zertifiziert', gdpr: 'DSGVO-Konform', pci: 'PCI-DSS Level 1' },
  it: { title: 'Perché le Aziende si Fidano di Noi', sub: 'Metriche verificate, certificazioni globali, pronti per enterprise.', clients: 'Clienti Globali', agents: 'Agenti Formati', languages: 'Lingue', csat: 'Punteggio CSAT', sla: 'SLA Disponibilità', deploy: 'Giorni di Avvio', iso: 'Certificazione ISO 27001', gdpr: 'Conforme GDPR', pci: 'PCI-DSS Livello 1' },
  pt: { title: 'Por Que as Empresas Confiam em Nós', sub: 'Métricas verificadas, certificações globais e prontidão enterprise.', clients: 'Clientes Globais', agents: 'Agentes Treinados', languages: 'Idiomas', csat: 'Pontuação CSAT', sla: 'SLA de Uptime', deploy: 'Dias para Lançamento', iso: 'Certificação ISO 27001', gdpr: 'Conforme GDPR', pci: 'PCI-DSS Nível 1' },
  nl: { title: 'Waarom Bedrijven Ons Vertrouwen', sub: 'Geverifieerde cijfers, wereldwijd gecertificeerd, enterprise-ready.', clients: 'Klanten Wereldwijd', agents: 'Getrainde Agenten', languages: 'Talen', csat: 'CSAT-score', sla: 'Uptime-SLA', deploy: 'Dagen tot Livegang', iso: 'ISO 27001 Gecertificeerd', gdpr: 'AVG-conform', pci: 'PCI-DSS Niveau 1' },
  pl: { title: 'Dlaczego Firmy Nam Ufają', sub: 'Zweryfikowane metryki, globalne certyfikacje, gotowość enterprise.', clients: 'Klienci na Świecie', agents: 'Przeszkoleni Agenci', languages: 'Języki', csat: 'Wynik CSAT', sla: 'SLA Dostępności', deploy: 'Dni do Startu', iso: 'Certyfikat ISO 27001', gdpr: 'Zgodność z RODO', pci: 'PCI-DSS Poziom 1' },
  ru: { title: 'Почему Компании Нам Доверяют', sub: 'Проверенные метрики, международные сертификаты, уровень enterprise.', clients: 'Клиенты по всему миру', agents: 'Обученные операторы', languages: 'Языки', csat: 'Оценка CSAT', sla: 'SLA Доступности', deploy: 'Дней до запуска', iso: 'Сертификат ISO 27001', gdpr: 'Соответствие GDPR', pci: 'PCI-DSS Уровень 1' },
  zh: { title: '企业为何信任我们', sub: '可验证指标、全球认证、企业级交付能力。', clients: '全球客户', agents: '受训坐席', languages: '语言', csat: 'CSAT评分', sla: '可用性SLA', deploy: '上线天数', iso: 'ISO 27001认证', gdpr: '符合GDPR', pci: 'PCI-DSS 一级' },
  ja: { title: '企業が私たちを信頼する理由', sub: '検証済みの指標、国際認証、エンタープライズ対応。', clients: '世界中の顧客', agents: '訓練済みエージェント', languages: '対応言語', csat: 'CSATスコア', sla: '稼働率SLA', deploy: '導入日数', iso: 'ISO 27001認証', gdpr: 'GDPR準拠', pci: 'PCI-DSS レベル1' },
  ko: { title: '기업이 우리를 신뢰하는 이유', sub: '검증된 지표, 글로벌 인증, 엔터프라이즈 준비 완료.', clients: '전 세계 고객', agents: '훈련된 에이전트', languages: '지원 언어', csat: 'CSAT 점수', sla: '가동률 SLA', deploy: '도입 일수', iso: 'ISO 27001 인증', gdpr: 'GDPR 준수', pci: 'PCI-DSS 레벨 1' },
  tr: { title: 'Şirketler Neden Bize Güveniyor', sub: 'Doğrulanmış metrikler, küresel sertifikalar, kurumsal hazır yapı.', clients: 'Dünya Çapında Müşteriler', agents: 'Eğitimli Ajanlar', languages: 'Diller', csat: 'CSAT Skoru', sla: 'Uptime SLA', deploy: 'Canlıya Geçiş Günü', iso: 'ISO 27001 Sertifikalı', gdpr: 'GDPR Uyumlu', pci: 'PCI-DSS Seviye 1' },
  sv: { title: 'Varför Företag Litar på Oss', sub: 'Verifierade mätetal, globala certifieringar, enterprise-redo.', clients: 'Kunder Globalt', agents: 'Utbildade Agenter', languages: 'Språk', csat: 'CSAT-poäng', sla: 'Drifttids-SLA', deploy: 'Dagar till Start', iso: 'ISO 27001-certifierad', gdpr: 'GDPR-kompatibel', pci: 'PCI-DSS Nivå 1' },
  da: { title: 'Hvorfor Virksomheder Stoler på Os', sub: 'Verificerede målinger, globale certificeringer, enterprise-klar.', clients: 'Kunder Globalt', agents: 'Trænede Agenter', languages: 'Sprog', csat: 'CSAT-score', sla: 'Oppetids-SLA', deploy: 'Dage til Go-Live', iso: 'ISO 27001-certificeret', gdpr: 'GDPR-kompatibel', pci: 'PCI-DSS Niveau 1' },
};

const STATS = [
  { value: '200+', icon: Users, key: 'clients' as const },
  { value: '500+', icon: Globe, key: 'agents' as const },
  { value: '12+', icon: Globe, key: 'languages' as const },
  { value: '95%+', icon: Award, key: 'csat' as const },
  { value: '99.9%', icon: Shield, key: 'sla' as const },
  { value: '7', icon: Clock, key: 'deploy' as const },
];

const CERTS = [
  { key: 'iso' as const, icon: Shield },
  { key: 'gdpr' as const, icon: CheckCircle2 },
  { key: 'pci' as const, icon: Award },
];

interface ServiceEEATProofProps {
  serviceName?: string;
  category?: 'bpo' | 'digital' | 'consulting';
  keywords?: string[];
}

export function ServiceEEATProof({ serviceName, category = 'bpo' }: ServiceEEATProofProps) {
  const { language, isRTL } = useLanguage();
  const t = LABELS[language] || LABELS.en;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${SITE_URL}/#eeat-proof`,
    name: 'Call IT Dev',
    ...(serviceName ? { department: { '@type': 'Organization', name: `Call IT Dev — ${serviceName}` } } : {}),
    aggregateRating: { '@type': 'AggregateRating', ratingValue: '4.8', bestRating: '5', ratingCount: '200', reviewCount: '127' },
    hasCredential: [
      { '@type': 'EducationalOccupationalCredential', credentialCategory: 'certification', name: 'ISO 27001' },
      { '@type': 'EducationalOccupationalCredential', credentialCategory: 'certification', name: 'GDPR Compliance' },
      { '@type': 'EducationalOccupationalCredential', credentialCategory: 'certification', name: 'PCI-DSS Level 1' },
    ],
  };

  useSchemaDOM([schema], 'eeat-proof', [language, serviceName]);

  return (
    <>
      <section className="py-16 bg-muted/30" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="container max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">{t.title}</h2>
            <p className="text-muted-foreground mt-2 max-w-2xl mx-auto">{t.sub}</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-10">
            {STATS.map((stat, i) => (
              <motion.div
                key={stat.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
                viewport={{ once: true }}
                className="text-center p-4 rounded-xl bg-background border border-border"
              >
                <stat.icon className="w-5 h-5 mx-auto mb-2 text-primary" />
                <div className="text-2xl font-bold text-primary">{stat.value}</div>
                <div className="text-xs text-muted-foreground mt-1">{t[stat.key]}</div>
              </motion.div>
            ))}
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            {CERTS.map((cert) => (
              <div key={cert.key} className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20">
                <cert.icon className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-foreground">{t[cert.key]}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <DeferredSchemas delay={250}>
        <BreadcrumbAutoGenerator />
      </DeferredSchemas>

      {/* ServiceFAQAutoGenerator removed — service pages already inject FAQPage via SEOHead jsonLd to avoid duplicate FAQPage errors */}

      <DeferredSchemas delay={600}>
        {serviceName && <ServiceVideoSchema serviceName={serviceName} category={category} />}
      </DeferredSchemas>

      <DeferredSchemas delay={700}>
        {serviceName && <ServiceCompetitorSchema serviceName={serviceName} category={category} />}
      </DeferredSchemas>

      <DeferredSchemas delay={800}>
        {serviceName && <ServiceEnrichedSchemas serviceName={serviceName} category={category} />}
      </DeferredSchemas>

      <DeferredSchemas delay={900}>
        {serviceName && <ServiceAuthoritySchemas serviceName={serviceName} category={category} />}
      </DeferredSchemas>
    </>
  );
}
